import { useState } from 'react';
import { Download, Play } from 'lucide-react';
import { getResponsiveTextClasses, getResponsiveSpacingClasses } from '@/lib/responsive-utils';

interface CompanyVideoProps {
  youtubeVideoId?: string;
  cvPdfUrl?: string;
  thumbnailImage?: string;
  className?: string;
}

export function CompanyVideo({ 
  youtubeVideoId = 'we_7rSkX7WY',
  cvPdfUrl = '/documents/LIEC-CV.pdf',
  thumbnailImage = '/hero-slide-1.jpg', // Puedes cambiar esta ruta a tu imagen personalizada
  className = '' 
}: CompanyVideoProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  const handleDownloadCV = () => {
    window.open(cvPdfUrl, '_blank');
  };

  return (
    <section className={`${getResponsiveSpacingClasses('py', 1, 12, 16)} bg-white ${className}`}>
      <div className="w-full   mx-auto px-4 sm:px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Video Section - Left */}
          <div className="relative">
            <div className="relative aspect-video bg-liec-neutral-900  overflow-hidden ">
              {!isPlaying ? (
                <>
                  {/* Custom Video Thumbnail */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${thumbnailImage})`
                    }}
                  >
                    {/* Gradient overlay for better button visibility */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/40" />
                  </div>
                  
                  {/* Play Button */}
                  <button
                    onClick={() => setIsPlaying(true)}
                    className="absolute inset-0 flex items-center justify-center group cursor-pointer"
                    aria-label="Reproducir video de presentación"
                  >
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-liec-primary-500 rounded-full flex items-center justify-center shadow-2xl transform transition-all duration-300 group-hover:scale-110 group-hover:bg-liec-primary-600">
                      <Play className="w-10 h-10 md:w-12 md:h-12 text-white ml-1" fill="white" />
                    </div>
                  </button>
                </>
              ) : (
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0`}
                  title="Video de presentación LIEC"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-liec-primary-500/10 rounded-full blur-3xl -z-10" />
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-liec-secondary-500/10 rounded-full blur-3xl -z-10" />
          </div>

          {/* Content Section - Right */}
          <div className=  'space-y-6 text-center md:text-left'  >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 text-liec-primary-500 font-semibold text-sm uppercase tracking-wider">
              NOSOTROS
            
            
           <svg className='inline-flex items-center gap-2 ml-2' width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="0.330774" y1="5.41623" x2="11.9289" y2="11.1156" stroke="#E34C0E" stroke-width="1.5"/>
            <path d="M6.36253 13.292L12.4163 11.2088L10.5026 4.91892" stroke="#E34C0E" stroke-width="1.5"/>
            </svg>

</div>

            {/* Heading */}
            <h2 className={getResponsiveTextClasses(
              'text-2xl font-bold text-liec-neutral-900',
              'text-3xl',
              'text-4xl'
            )}>
              LIEC Expertos en{' '}
              <span className="text-liec-primary-500">Control de Calidad de Materiales</span>
              {' '}en México
            </h2>

            {/* Description */}
            <div className="space-y-4">
              <p className={`${getResponsiveTextClasses('text-base', 'text-lg', 'text-lg')} text-liec-neutral-1000 leading-relaxed`}>
                Experiencia y liderazgo en pruebas de materiales.
              </p>
              
              <p className={`${getResponsiveTextClasses('text-sm', 'text-base', 'text-base')} text-liec-neutral-1000 leading-relaxed`}>
                Fundada en 1988, somos pioneros en la industria, ofreciendo soluciones innovadoras y amigables con el ambiente.
              </p>
              
              <p className={`${getResponsiveTextClasses('text-sm', 'text-base', 'text-base')} text-liec-neutral-1000 leading-relaxed`}>
                Con más de 30 años de trayectoria, nos hemos consolidado como un referente en el control de calidad de materiales para la construcción en México.
              </p>
            </div>

            {/* Download CV Button */}
            <div className="pt-4">
              <button
                onClick={handleDownloadCV}
                className="inline-flex items-center gap-3 bg-liec-primary-500 hover:bg-liec-primary-600 text-white font-semibold px-4 py-3  transition-all duration-300   transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-liec-primary-500 focus:ring-offset-2"
              >
                <Download className="w-4 h-4" />
                DESCARGAR CV
              </button>
            </div>

            
          </div>

        </div>
      </div>
    </section>
  );
}
